import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useShopifyProducts } from "@/context/ShopifyProductsContext";
import { useCart } from "@/context/CartContext";
import { useCompare } from "@/context/CompareContext";
import type { Product } from "@/types/product";

// ─── spec rows ────────────────────────────────────────────────────────────────
const SPEC_ROWS: { label: string; key: string }[] = [
  { label: "Category",       key: "category" },
  { label: "Brand",          key: "brand" },
  { label: "Capacity",       key: "capacity" },
  { label: "Wattage / Output", key: "watts" },
  { label: "Price",          key: "price" },
  { label: "Savings",        key: "savings" },
  { label: "Availability",   key: "availability" },
];

function getCategoryLabel(cat: string) {
  if (cat === "solar") return "Solar Generator";
  if (cat === "gas") return "Gas Generator";
  if (cat === "whole-home") return "Whole-Home System";
  return cat;
}

function renderSpec(product: Product, key: string) {
  if (key === "category")
    return <span className="capitalize">{getCategoryLabel(product.category)}</span>;
  if (key === "brand")
    return <span className="font-semibold text-gray-900">{product.brand}</span>;
  if (key === "capacity")
    return product.capacity ? <span>{product.capacity}</span> : <span className="text-gray-300">—</span>;
  if (key === "watts")
    return product.watts ? <span>{product.watts}</span> : <span className="text-gray-300">—</span>;
  if (key === "price")
    return (
      <div className="flex flex-col items-center">
        <span className={`font-extrabold text-xl ${product.originalPrice ? "text-red-600" : "text-gray-900"}`}>
          ${product.price.toLocaleString()}
        </span>
        {product.originalPrice && (
          <span className="text-xs text-gray-400 line-through mt-0.5">
            ${product.originalPrice.toLocaleString()}
          </span>
        )}
      </div>
    );
  if (key === "savings") {
    if (!product.originalPrice) return <span className="text-gray-300">—</span>;
    const save = product.originalPrice - product.price;
    const pct = Math.round((save / product.originalPrice) * 100);
    return (
      <span className="inline-flex items-center gap-1 bg-red-50 text-red-600 text-xs font-bold px-2.5 py-1 rounded-md">
        Save ${save.toLocaleString()} ({pct}% off)
      </span>
    );
  }
  if (key === "availability")
    return product.inStock ? (
      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
        <i className="ri-checkbox-circle-fill"></i> In Stock
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-gray-400 text-xs font-semibold">
        <i className="ri-close-circle-fill"></i> Out of Stock
      </span>
    );
  return <span className="text-gray-300">—</span>;
}

// ─── star rating ──────────────────────────────────────────────────────────────
function StarRow({ rating, reviews }: { rating: number; reviews: number }) {
  return (
    <div className="flex items-center gap-1 justify-center mt-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`text-xs ${
            s <= Math.floor(rating)
              ? "ri-star-fill text-amber-400"
              : s - rating < 1
              ? "ri-star-half-fill text-amber-400"
              : "ri-star-line text-gray-300"
          }`}
        />
      ))}
      <span className="text-xs text-gray-400 ml-0.5">({reviews.toLocaleString()})</span>
    </div>
  );
}

// ─── page ─────────────────────────────────────────────────────────────────────
export default function ComparePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { products, loading } = useShopifyProducts();
  const { addItem } = useCart();
  const { toggleCompare, isInCompare } = useCompare();

  const [copied, setCopied] = useState(false);

  const rawIds = searchParams.get("ids") ?? "";
  const ids = rawIds ? rawIds.split(",").filter(Boolean) : [];

  const compareProducts: Product[] = ids
    .map((id) => products.find((p) => p.id === id))
    .filter((p): p is Product => Boolean(p));

  // Sync URL: if the /compare page is opened with no ids but compare list has items, redirect
  const colWidth = compareProducts.length === 2 ? "w-1/2" : "w-1/3";

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const handleAddToCart = (product: Product) => {
    if (!product.inStock) return;
    addItem({ id: product.id, name: product.name, brand: product.brand, price: product.price, image: product.image });
  };

  // Loading state
  if (loading) {
    return (
      <main className="font-sans bg-white min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-40 text-center">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 animate-spin text-amber-500">
            <i className="ri-loader-4-line text-4xl"></i>
          </div>
          <p className="text-gray-400 text-sm">Loading products...</p>
        </div>
        <Footer />
      </main>
    );
  }

  // Empty / not found state
  if (ids.length === 0 || compareProducts.length === 0) {
    return (
      <main className="font-sans bg-white min-h-screen">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-40 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5 text-gray-200">
            <i className="ri-scales-3-line text-6xl"></i>
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900 mb-2">Nothing to compare yet</h1>
          <p className="text-gray-500 text-sm mb-8 max-w-md mx-auto">
            Select 2–3 products on the shop page and hit "Compare Now" — or paste a shared compare link here.
          </p>
          <button
            onClick={() => navigate("/shop")}
            className="bg-amber-500 hover:bg-amber-400 text-white font-bold px-7 py-3 rounded-xl transition-colors cursor-pointer"
          >
            Browse Products
          </button>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="font-sans bg-white min-h-screen">
      <Navbar />
      <div className="pt-20 md:pt-24" />

      {/* Page header */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 border-b border-gray-100">
        <nav className="flex items-center gap-1.5 text-xs text-gray-400 mb-4 flex-wrap">
          <Link to="/" className="hover:text-amber-500 transition-colors cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/shop" className="hover:text-amber-500 transition-colors cursor-pointer">Shop</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-gray-700 font-medium">Compare Products</span>
        </nav>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900">Side-by-Side Comparison</h1>
            <p className="text-sm text-gray-500 mt-1">
              Comparing <span className="font-semibold text-gray-700">{compareProducts.length} products</span>
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Share URL */}
            <button
              onClick={handleShare}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                copied
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={copied ? "ri-check-line" : "ri-link"}></i>
              </div>
              {copied ? "Link Copied!" : "Copy Share Link"}
            </button>

            {/* Back to shop */}
            <Link
              to="/shop"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-white text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </div>
              Add More Products
            </Link>
          </div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="border border-gray-200 rounded-2xl overflow-hidden">

          {/* Product header row */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            <div className="w-36 shrink-0 px-5 py-6 border-r border-gray-200">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-widest">Product</p>
            </div>
            {compareProducts.map((product) => (
              <div key={product.id} className={`${colWidth} px-5 py-5 border-r border-gray-200 last:border-r-0 flex flex-col items-center text-center`}>
                <div className="w-full h-40 bg-white rounded-xl overflow-hidden mb-4 flex items-center justify-center border border-gray-100">
                  <img src={product.image} alt={product.name} className="w-full h-full object-contain p-4" />
                </div>
                <p className="text-[11px] text-gray-400 font-semibold uppercase tracking-wider mb-0.5">{product.brand}</p>
                <Link
                  to={`/shop/${product.id}`}
                  className="font-bold text-gray-900 text-sm leading-snug hover:text-amber-600 transition-colors cursor-pointer line-clamp-2 text-center"
                >
                  {product.name}
                </Link>
                <StarRow rating={product.rating} reviews={product.reviews} />

                {/* Compare toggle chip */}
                <button
                  onClick={() => toggleCompare(product)}
                  className={`mt-3 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border ${
                    isInCompare(product.id)
                      ? "border-amber-300 bg-amber-50 text-amber-700"
                      : "border-gray-200 text-gray-500 hover:border-amber-300 hover:text-amber-600"
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <i className={`text-xs ${isInCompare(product.id) ? "ri-check-line" : "ri-scales-3-line"}`}></i>
                    {isInCompare(product.id) ? "In Compare Tray" : "Add to Tray"}
                  </span>
                </button>
              </div>
            ))}
          </div>

          {/* Spec rows */}
          {SPEC_ROWS.map((row, i) => (
            <div
              key={row.key}
              className={`flex border-b border-gray-100 last:border-b-0 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}
            >
              <div className="w-36 shrink-0 px-5 py-4 bg-gray-50 border-r border-gray-200 flex items-center">
                <p className="text-xs font-semibold text-gray-500 whitespace-nowrap">{row.label}</p>
              </div>
              {compareProducts.map((product) => (
                <div
                  key={product.id}
                  className={`${colWidth} px-5 py-4 border-r border-gray-100 last:border-r-0 flex items-center justify-center text-center text-sm text-gray-700`}
                >
                  {renderSpec(product, row.key)}
                </div>
              ))}
            </div>
          ))}

          {/* Highlights */}
          <div className="flex border-b border-gray-100 bg-white">
            <div className="w-36 shrink-0 px-5 py-5 bg-gray-50 border-r border-gray-200 flex items-start">
              <p className="text-xs font-semibold text-gray-500 pt-0.5">Highlights</p>
            </div>
            {compareProducts.map((product) => (
              <div key={product.id} className={`${colWidth} px-5 py-5 border-r border-gray-100 last:border-r-0`}>
                <ul className="space-y-2">
                  {product.highlights.map((h, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                      <div className="w-3.5 h-3.5 flex items-center justify-center mt-0.5 shrink-0">
                        <i className="ri-check-line text-emerald-500 text-xs"></i>
                      </div>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Trust row */}
          <div className="flex border-b border-gray-100 bg-gray-50/40">
            <div className="w-36 shrink-0 px-5 py-4 bg-gray-50 border-r border-gray-200 flex items-center">
              <p className="text-xs font-semibold text-gray-500">Included</p>
            </div>
            {compareProducts.map((product) => (
              <div key={product.id} className={`${colWidth} px-5 py-4 border-r border-gray-100 last:border-r-0 flex flex-col items-center gap-1.5`}>
                <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                  <i className="ri-truck-line"></i> Free Shipping
                </span>
                <span className="flex items-center gap-1.5 text-xs text-amber-600 font-medium">
                  <i className="ri-shield-check-line"></i> Mfr. Warranty
                </span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                  <i className="ri-arrow-go-back-line"></i> 30-Day Returns
                </span>
              </div>
            ))}
          </div>

          {/* Add to Cart row */}
          <div className="flex bg-white border-t border-gray-200">
            <div className="w-36 shrink-0 bg-gray-50 border-r border-gray-200 flex items-center px-5">
              <p className="text-xs font-semibold text-gray-500 whitespace-nowrap">Add to Cart</p>
            </div>
            {compareProducts.map((product) => (
              <div key={product.id} className={`${colWidth} px-5 py-5 border-r border-gray-100 last:border-r-0 flex flex-col items-center gap-2.5`}>
                <button
                  onClick={() => handleAddToCart(product)}
                  disabled={!product.inStock}
                  className={`w-full py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                    product.inStock
                      ? "bg-amber-500 hover:bg-amber-400 text-white"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {product.inStock ? "Add to Cart" : "Out of Stock"}
                </button>
                <Link
                  to={`/shop/${product.id}`}
                  className="text-xs text-gray-500 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  View Full Details →
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-8 pt-6 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Need help choosing?{" "}
            <a href="tel:8666054975" className="text-amber-500 font-semibold hover:underline cursor-pointer">
              Call (866) 605-4975
            </a>
            {" "}— our experts are ready.
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                copied
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-gray-200 text-gray-600 hover:border-amber-300 hover:text-amber-600"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={copied ? "ri-check-line" : "ri-share-line"}></i>
              </div>
              {copied ? "Copied!" : "Share Comparison"}
            </button>
            <Link
              to="/shop"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              Back to Shop
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
